import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PROCESS_VIDEOS } from "../data/site";

const ease = [0.16, 1, 0.3, 1];

/* One scroll-driven "opening window" that reveals a fabrication video */
const WindowReveal = ({ data, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // Panels slide apart between 15% and 70% of the scroll
  const leftX = useTransform(scrollYProgress, [0.12, 0.62], ["0%", "-101%"]);
  const rightX = useTransform(scrollYProgress, [0.12, 0.62], ["0%", "101%"]);
  const frameScale = useTransform(scrollYProgress, [0.12, 0.7], [0.92, 1]);
  const videoScale = useTransform(scrollYProgress, [0.12, 0.75], [1.25, 1]);
  const capOpacity = useTransform(scrollYProgress, [0.55, 0.75], [0, 1]);
  const shutterTextOpacity = useTransform(scrollYProgress, [0.1, 0.32], [1, 0]);

  const reversed = index % 2 === 1;

  return (
    <div ref={ref} data-testid={`window-reveal-${data.id}`} className="relative h-[260vh]">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        <div className="max-w-7xl w-full mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
          {/* Text side */}
          <motion.div
            style={{ opacity: capOpacity }}
            className={`lg:col-span-4 ${reversed ? "lg:order-2" : ""}`}
          >
            <p className="eyebrow mb-4">{data.label}</p>
            <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight text-paper">
              {data.title}
            </h3>
            <p className="mt-5 text-paper/55 text-sm md:text-base leading-relaxed">
              {data.desc}
            </p>
          </motion.div>

          {/* The window */}
          <div className={`lg:col-span-8 ${reversed ? "lg:order-1" : ""}`}>
            <motion.div
              style={{ scale: frameScale }}
              className="relative mx-auto w-full aspect-[16/10] max-h-[72vh] p-3 md:p-4 bg-gold/90 shadow-2xl"
            >
              {/* Inner frame */}
              <div className="relative w-full h-full overflow-hidden bg-navy-deep border-2 border-navy">
                {/* The revealed video */}
                <motion.video
                  style={{ scale: videoScale }}
                  className="absolute inset-0 w-full h-full object-cover"
                  src={data.src}
                  poster={data.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <div className="absolute inset-0 bg-navy/10 pointer-events-none" />

                {/* Caption inside */}
                <motion.div
                  style={{ opacity: capOpacity }}
                  className="absolute bottom-0 left-0 right-0 p-5 md:p-7 bg-gradient-to-t from-navy-deep/90 to-transparent"
                >
                  <span className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.25em] text-gold-soft">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> In lavorazione
                  </span>
                </motion.div>

                {/* LEFT shutter panel */}
                <motion.div
                  style={{ x: leftX }}
                  className="shutter-panel absolute inset-y-0 left-0 w-1/2 z-20 border-r border-navy-deep flex items-center justify-end pr-2"
                >
                  <span className="block w-[3px] h-16 rounded bg-gold/40" />
                </motion.div>

                {/* RIGHT shutter panel */}
                <motion.div
                  style={{ x: rightX }}
                  className="shutter-panel absolute inset-y-0 right-0 w-1/2 z-20 border-l border-navy-deep flex items-center justify-start pl-2"
                >
                  <span className="block w-[3px] h-16 rounded bg-gold/40" />
                </motion.div>

                {/* Text over closed shutters */}
                <motion.div
                  style={{ opacity: shutterTextOpacity }}
                  className="absolute inset-0 z-30 flex flex-col items-center justify-center pointer-events-none"
                >
                  <p className="eyebrow text-gold-soft mb-3">Scorri per aprire</p>
                  <p className="font-serif text-2xl md:text-3xl text-paper/90 italic">{data.label}</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProcessReveal = () => {
  return (
    <section id="processo" data-testid="process-section" className="relative bg-navy text-paper grain">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 text-center">
        <p className="eyebrow mb-5">Il nostro processo</p>
        <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight">
          Guarda come nasce
          <span className="italic text-gold"> ogni opera</span>
        </h2>
        <p className="mt-6 text-paper/55 max-w-xl mx-auto text-sm md:text-base">
          Scorri verso il basso: le persiane si aprono e svelano la lavorazione,
          dal primo taglio fino alla finitura.
        </p>
      </div>

      {PROCESS_VIDEOS.map((v, i) => (
        <WindowReveal key={v.id} data={v} index={i} />
      ))}
    </section>
  );
};
